import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Box } from 'reflexbox';
import styled from 'styled-components';
import { useState } from 'react';

import {
	ArchiveIcon,
	ChevronLeftIcon,
	GroupsIcon,
	PrintIcon,
	ShareIcon,
	UndoIcon,
} from '@treams/treams-icons';
import {
	Button,
	Callout,
	Container,
	Modal,
	Paragraph,
	Popover,
	Sidebar,
	SidebarLayout,
	Skeleton,
	Spinner,
	Subheading,
	Title,
	Toolbar,
} from '@treams/treams-ui';
import { useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';

import FeedbackRecipient from './FeedbackRecipient';
import FeedbackResultQuestion from './FeedbackResultQuestion';
import FeedbackRadarChart from './FeedbackRadarChart';

import { UserSelectorButton } from '../../packages/kakapo-ui-kit/components/users/UserCollaboratorsSelector';
import { Recipient } from '../../packages/kakapo-ui-services/models/Recipient';
import RecipientSelector from '../../packages/kakapo-ui-kit/components/recipients/RecipientSelector';
import { FeedbackShareModal } from './FeedbackShareModal';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';

const SidebarContainer = styled(Box)`
	background: ${(props) => props.theme.colors.whiteBlue};
	height: 100%;

	@media print {
		display: none;
	}
`;

const StyledToolbar = styled(Toolbar)`
	@media print {
		display: none;
	}
`;

function Main(props: { request: graphql.FeedbackRequest }) {
	const { t } = useTranslation();
	const location = useLocation();
	const history = useHistory();

	const [archive, { loading: isLoadingArchive }] = graphql.useArchiveFeedbackRequestMutation({
		variables: { input: { id: props.request.id } },
	});
	const [restore, { loading: isLoadingRestore }] = graphql.useRestoreFeedbackRequestMutation({
		variables: { input: { id: props.request.id } },
	});

	const [sharing, setSharing] = useState(false);
	const hasSharedFeedback = useFeature(graphql.FeatureName.FeedbackShare);

	const responses = _.sortBy(
		props.request.responses.edges.map(({ node }) => node),
		(response) => `${response.recipient.firstName} ${response.recipient.lastName}`,
	);

	const responseID = new URLSearchParams(location.search).get('response_id');
	const response = responseID ? responses.find(({ id }) => id === responseID) : undefined;

	const returnUrl: string = (() => {
		const params = new URLSearchParams(history.location.search);
		if (params.has('return') && params.get('return')?.startsWith('/')) {
			return params.get('return');
		}
		return `/users/${props.request.user.id}/feedback`;
	})()!;

	return (
		<Container py={4}>
			{props.request.isArchived && (
				<Callout
					mb={3}
					color="red"
					icon={<ArchiveIcon />}
					title={t('feedback.show.archived.title')}
					description={t('feedback.show.archived.description')}
				/>
			)}
			<StyledToolbar>
				<Button
					icon={<ChevronLeftIcon />}
					link={returnUrl}
					title={t('feedback.show.toolbar.back')}
					type="secondary"
				/>
				<Toolbar.Spacing />
				{props.request.user.isViewer && hasSharedFeedback && (
					<Button
						icon={<ShareIcon />}
						type="secondary"
						title={t('common.share')}
						onClick={() => setSharing(true)}
					/>
				)}
				<Button
					icon={<PrintIcon />}
					type="secondary"
					title={t('feedback.show.toolbar.print')}
					onClick={() => window.print()}
				/>
				<Button
					title={
						props.request.isArchived
							? t('feedback.show.toolbar.restore')
							: t('feedback.show.toolbar.archive')
					}
					type="destructive"
					icon={props.request.isArchived ? <UndoIcon /> : <ArchiveIcon />}
					loading={isLoadingArchive || isLoadingRestore}
					onClick={() => {
						if (props.request.isArchived) {
							restore();
						} else {
							archive();
						}
					}}
				/>
			</StyledToolbar>
			<Box mt={4}>
				<Title mb={3}>{props.request.title || t('feedback.incoming.card.title')}</Title>
				{props.request.instruction && <QuillText quill={props.request.instruction} />}
				{props.request.questions.map((question) => (
					<Box mt={4} key={question.id}>
						<FeedbackResultQuestion
							request={props.request}
							question={question}
							response={response ?? undefined}
							responses={responses}
						/>
					</Box>
				))}
			</Box>
			{sharing && (
				<FeedbackShareModal feedbackRequest={props.request} onDone={() => setSharing(false)} />
			)}
		</Container>
	);
}

function MainSkeleton() {
	const { t } = useTranslation();

	return (
		<Container py={4}>
			<Toolbar>
				<Button
					icon={<ChevronLeftIcon />}
					link={'/feedback'}
					title={t('feedback.show.toolbar.back')}
					type="secondary"
				/>
			</Toolbar>
			<Box mt={4}>
				<Title.Skeleton mb={3} />
				<Paragraph.Skeleton />
				<Skeleton.Repeat count={3}>
					<Box mt={4}>
						<FeedbackResultQuestion.Skeleton />
					</Box>
				</Skeleton.Repeat>
			</Box>
		</Container>
	);
}

function FeedbackAddRecipientsModal({
	recipients,
	onAddRecipients,
	onCancel,
}: {
	recipients: Recipient[];
	onAddRecipients: (recipients: Recipient[]) => void;
	onCancel: () => void;
}) {
	const { t } = useTranslation();

	const [newRecipients, setNewRecipients] = useState<Recipient[]>([]);

	return (
		<Popover visible center>
			<Modal>
				<Modal.Header title={t('common.feedback.addRecipients')} />

				<Modal.Contents>
					<RecipientSelector
						initialRecipients={newRecipients}
						excludeRecipients={recipients}
						onChange={setNewRecipients}
						footer={null}
					/>
				</Modal.Contents>
				<Modal.FormFooter
					onCancel={onCancel}
					disabled={newRecipients.length === 0}
					saveTitle={t('common.feedback.nextStepSend')}
					onSave={() => onAddRecipients(newRecipients)}
				/>
			</Modal>
		</Popover>
	);
}

function FeedbackRecipients(props: { request: graphql.FeedbackRequest }) {
	const { t } = useTranslation();

	const [selectorVisible, setSelectorVisible] = useState(false);

	const [internal, { loading: internalLoading }] =
		graphql.useRequestAdditionalInternalFeedbackMutation();

	const [external, { loading: externalLoading }] =
		graphql.useRequestAdditionalExternalFeedbackMutation();

	const [restore, { loading: loadingRestore }] = graphql.useRestoreFeedbackResponseMutation();

	const loading = internalLoading || externalLoading || loadingRestore;

	const sorted = _.sortBy(
		props.request.responses.edges,
		({ node: { recipient } }) => `${recipient.firstName} ${recipient.lastName}`,
	);

	const filtered = sorted.filter(
		({ node }) =>
			!(node.status === graphql.FeedbackResponseStatus.Cancelled && node.answers?.length === 0),
	);

	return (
		<Box>
			<Box>
				<Subheading>{t('feedback.results.questions.respondents.title')}</Subheading>
				<Paragraph>{t('feedback.results.questions.respondents.description')}</Paragraph>
			</Box>
			<Box mt={3}>
				<Box mt={2}>
					<Sidebar.Item
						small
						icon={<GroupsIcon />}
						title={t('feedback.results.questions.respondents.all')}
						path={
							props.request.user.isViewer
								? `/feedback/${props.request.id}`
								: `/users/${props.request.user.id}/feedback/${props.request.id}`
						}
						exact
					/>
				</Box>
				{filtered.map(({ node }) => (
					<Box key={node.id} mt={2}>
						<FeedbackRecipient
							request={props.request}
							response={node as graphql.FeedbackResponse}
						/>
					</Box>
				))}
				<Box pt={2} px={3}>
					{loading ? <Spinner /> : <UserSelectorButton onClick={() => setSelectorVisible(true)} />}

					{selectorVisible && (
						<FeedbackAddRecipientsModal
							recipients={filtered.map(
								({ node: { recipient } }): Recipient =>
									'id' in recipient
										? {
												userId: recipient.id,
												userData: recipient as graphql.User,
										  }
										: {
												name: `${recipient.firstName} ${recipient.lastName}`,
												email: recipient.email,
										  },
							)}
							onAddRecipients={async (recipients) => {
								setSelectorVisible(false);

								for (const recipient of recipients) {
									if ('userId' in recipient) {
										// Can potentially be a cancelled invitee
										const responseID = sorted.find(
											({ node }) =>
												'id' in node.recipient && node.recipient.id === recipient.userId,
										)?.node.id;

										if (!responseID) {
											await internal({
												variables: {
													input: { requestID: props.request.id, userID: recipient.userId },
												},
											});
										} else {
											// Restore invitee instead
											await restore({
												variables: {
													input: { responseID },
												},
											});
										}
									} else {
										// Can potentially be a cancelled invitee
										const responseID = sorted.find(
											({ node }) =>
												'email' in node.recipient && node.recipient.email === recipient.email,
										)?.node.id;

										if (!responseID) {
											const [firstName, ...lastName] = recipient.name.split(' ');

											await external({
												variables: {
													input: {
														requestID: props.request.id,
														email: recipient.email,
														firstName,
														lastName: lastName.join(' '),
													},
												},
											});
										} else {
											// Restore invitee instead
											await restore({
												variables: {
													input: { responseID },
												},
											});
										}
									}
								}
							}}
							onCancel={() => setSelectorVisible(false)}
						/>
					)}
				</Box>
			</Box>
		</Box>
	);
}

function FeedbackRecipientsSkeleton() {
	const { t } = useTranslation();

	return (
		<Box>
			<Subheading>{t('feedback.results.questions.respondents.title')}</Subheading>
			<Paragraph>{t('feedback.results.questions.respondents.description')}</Paragraph>
			<Box mt={3}>
				<Skeleton.Repeat count={3}>
					<Box mt={2}>
						<FeedbackRecipient.Skeleton />
					</Box>
				</Skeleton.Repeat>
			</Box>
		</Box>
	);
}

function FeedbackSidebar(props: { request: graphql.FeedbackRequest }) {
	const { t } = useTranslation();

	return (
		<SidebarContainer p={3}>
			{props.request.questions.some(
				(question) => question.type !== graphql.FeedbackQuestionType.Text,
			) && (
				<Box mb={3}>
					<Subheading>{t('feedback.results.questions.chart.title')}</Subheading>
					<Paragraph>{t('feedback.results.questions.chart.description')}</Paragraph>
					<Box mt={2}>
						<FeedbackRadarChart request={props.request} />
					</Box>
				</Box>
			)}
			<Box>
				<FeedbackRecipients request={props.request} />
			</Box>
		</SidebarContainer>
	);
}

function FeedbackSidebarSkeleton() {
	const { t } = useTranslation();

	return (
		<SidebarContainer p={3}>
			<Box>
				<Subheading>{t('feedback.results.questions.chart.title')}</Subheading>
				<Paragraph>{t('feedback.results.questions.chart.description')}</Paragraph>
				<Box mt={2}>
					<FeedbackRadarChart.Skeleton />
				</Box>
			</Box>
			<Box mt={3}>
				<FeedbackRecipientsSkeleton />
			</Box>
		</SidebarContainer>
	);
}

function FeedbackRequest(props: { request: graphql.FeedbackRequest }) {
	return (
		<SidebarLayout sidebar={<FeedbackSidebar request={props.request} />} right>
			<Main request={props.request} />
		</SidebarLayout>
	);
}

function FeedbackRequestSkeleton() {
	return (
		<SidebarLayout sidebar={<FeedbackSidebarSkeleton />} right>
			<MainSkeleton />
		</SidebarLayout>
	);
}

export default function FeedbackRequestByID(props: { id: string }) {
	const query = graphql.useFeedbackResultsQuery({ variables: { id: props.id } });

	return query.data ? (
		<FeedbackRequest request={query.data.feedbackRequest as graphql.FeedbackRequest} />
	) : (
		<FeedbackRequestSkeleton />
	);
}
